/* eslint-disable react/prop-types */
import React from 'react';
import { Modal, Table, Button, Tag, Typography, Space, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const CsvUpdatePreviewModal = ({
  visible,
  onCancel,
  onConfirm,
  previewData,
  isLoading = false,
}) => {
  // Calculate statistics
  const totalRows = previewData?.length || 0;
  const foundRows = previewData?.filter((item) => item.found).length || 0;
  const notFoundRows = totalRows - foundRows;

  // Prepare table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'ID',
      key: 'ID',
      width: 100,
      fixed: 'left',
    },
    {
      title: 'Status',
      dataIndex: 'found',
      key: 'status',
      width: 100,
      render: (found) => (
        <Tag color={found ? 'green' : 'red'} icon={found ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {found ? 'Found' : 'Not Found'}
        </Tag>
      ),
    },
    {
      title: 'Joint Reference',
      key: 'jointReference',
      children: [
        {
          title: 'Current',
          key: 'currentJointRef',
          width: 150,
          render: (_, record) => <Text>{record.current?.JointReference || '-'}</Text>,
        },
        {
          title: 'New',
          key: 'newJointRef',
          width: 150,
          render: (_, record) => (
            <Text strong style={{ color: '#1890ff' }}>
              {record.new?.JointReference || '-'}
            </Text>
          ),
        },
      ],
    },
    {
      title: 'Wall Thickness',
      key: 'wallThickness',
      children: [
        {
          title: 'Current',
          key: 'currentWallThick',
          width: 150,
          render: (_, record) => <Text>{record.current?.WallThickness || '-'}</Text>,
        },
        {
          title: 'New',
          key: 'newWallThick',
          width: 150,
          render: (_, record) => (
            <Text strong style={{ color: '#1890ff' }}>
              {record.new?.WallThickness || '-'}
            </Text>
          ),
        },
      ],
    },
    {
      title: 'Pipe Diameter',
      key: 'pipeDiameter',
      children: [
        {
          title: 'Current',
          key: 'currentPipeDia',
          width: 150,
          render: (_, record) => <Text>{record.current?.PipeDiameter || '-'}</Text>,
        },
        {
          title: 'New',
          key: 'newPipeDia',
          width: 150,
          render: (_, record) => (
            <Text strong style={{ color: '#1890ff' }}>
              {record.new?.PipeDiameter || '-'}
            </Text>
          ),
        },
      ],
    },
  ];

  return (
    <Modal
      title="Preview CSV Update"
      visible={visible}
      onCancel={onCancel}
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
          loading={isLoading}
          disabled={foundRows === 0}
        >
          Confirm Update ({foundRows} records)
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Summary Statistics */}
        <div>
          <Title level={5}>Summary</Title>
          <Space size="large">
            <Text>
              <strong>Total Rows:</strong> {totalRows}
            </Text>
            <Text type="success">
              <strong>Will Update:</strong> {foundRows}
            </Text>
            <Text type="warning">
              <strong>Will Skip:</strong> {notFoundRows}
            </Text>
          </Space>
        </div>

        {/* Warning for not found IDs */}
        {notFoundRows > 0 && (
          <Alert
            message={`${notFoundRows} record(s) will be skipped because their IDs were not found in the table.`}
            type="warning"
            showIcon
            closable
          />
        )}

        {/* Preview Table */}
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Table
            columns={columns}
            dataSource={previewData}
            rowKey="ID"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`,
            }}
            scroll={{ x: 1200, y: '50vh' }}
            size="small"
          />
        </div>

        {/* Info Message */}
        <Alert
          message="Only records with matching IDs will be updated. Records with IDs not found in the table will be skipped."
          type="info"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default CsvUpdatePreviewModal;
